import React, {Component} from 'react'
import {split} from 'lodash'
import {withNamespaces, Trans} from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import FormHelperText from '@material-ui/core/FormHelperText'
import withStyles from '@material-ui/core/styles/withStyles'
import HeaderDialog from '../Common/Dialog/HeaderDialog'
import PageTitle from '../Common/PageTitle'
import LoadingButton from '../Common/LoadingButton'
import NotificationBar from '../Common/NotificationBar'
import messages from '../../assets/messages'
import {config} from '../../config'
import {
  partnerLegalAcceptanceFinish,
  partnerLegalAcceptancePreview,
} from '../../common/utils/requests'
import {TauroTermsCheckbox} from '../Common/TauroTermsCheckbox'
import {TauroRulesModal} from '../Common/TauroRulesModal'

const styles = (theme) => ({
  mainBody: {
    maxWidth: theme.mainBodyWidth,
    margin: 'auto',
    position: 'relative' as const,
  },
  wrapper: {
    padding: theme.spacing(1),
    maxWidth: 560,
    margin: 30,
  },
  grid: {
    marginTop: 16,
    marginBottom: 16,
  },
  link: {
    color: theme.palette.primary.main,
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginTop: 12,
  },
  checkboxLabel: {
    fontSize: '0.9rem',
  },
})

/**
 * Standalone Partner-API legal consent page (PT-5859 / PT-5860).
 * Opened from the tokenized registration response URL — no login required.
 */
class PartnerLegalAcceptance extends Component<any, any> {
  constructor(props) {
    super(props)
    const token = split(props.history.location.pathname, '/')[2] || ''
    const links = (config.legalLinks || config.weblinks || {}) as {
      terms?: string
      legal?: string
      legalDocuments?: string
      privacy?: string
      privacyPolicy?: string
      termsAndConditions?: string
    }
    this.state = {
      token,
      loading: true,
      submitting: false,
      formStatus: '',
      errorText: '',
      alreadyAccepted: false,
      acceptTerms: false,
      formError: false,
      tauroFactorisation: null,
      tauroTermsAccepted: false,
      tauroRulesOpen: false,
      cashbackDisclaimer: null,
      cashbackDisclaimerAccepted: false,
      // PT-7172
      requiresEuSolicitationConsent: false,
      solicitationAcceptance: false,
      links: {
        terms: links.terms || links.termsAndConditions,
        legal: links.legal || links.legalDocuments,
        privacy: links.privacy || links.privacyPolicy,
      },
    }
  }

  componentDidMount() {
    const {token} = this.state
    if (!token) {
      this.setState({loading: false, formStatus: 'failure', errorText: 'invalidToken'})
      return
    }
    partnerLegalAcceptancePreview(token)
      .then((data) => {
        this.setState({
          loading: false,
          alreadyAccepted: !!data.alreadyAccepted,
          formStatus: data.alreadyAccepted ? 'success' : '',
          links: {
            terms: data.links?.terms || this.state.links.terms,
            legal: data.links?.legal || this.state.links.legal,
            privacy: data.links?.privacy || this.state.links.privacy,
          },
          tauroFactorisation:
            data.tauroFactorisation?.required || data.tauroFactorisation?.enabled
              ? data.tauroFactorisation
              : null,
          cashbackDisclaimer: data.cashbackDisclaimer?.required ? data.cashbackDisclaimer : null,
          requiresEuSolicitationConsent: !!data.requiresEuSolicitationConsent,
        })
      })
      .catch(() => {
        this.setState({loading: false, formStatus: 'failure', errorText: 'invalidToken'})
      })
  }

  handleSubmit() {
    const {
      token,
      acceptTerms,
      tauroFactorisation,
      tauroTermsAccepted,
      cashbackDisclaimer,
      cashbackDisclaimerAccepted,
      requiresEuSolicitationConsent,
      solicitationAcceptance,
    } = this.state
    const tauroRequired = !!tauroFactorisation
    const cashbackRequired = !!cashbackDisclaimer
    if (
      !acceptTerms ||
      (tauroRequired && !tauroTermsAccepted) ||
      (cashbackRequired && !cashbackDisclaimerAccepted) ||
      (requiresEuSolicitationConsent && !solicitationAcceptance)
    ) {
      this.setState({formError: true})
      return
    }
    this.setState({submitting: true, formError: false, errorText: ''})
    partnerLegalAcceptanceFinish(token, {
      ...(tauroRequired ? {tauroTermsAccepted: true} : {}),
      ...(cashbackRequired ? {cashbackDisclaimerAcceptance: true} : {}),
      ...(requiresEuSolicitationConsent ? {solicitationAcceptance: true} : {}),
    })
      .then(() => {
        this.setState({submitting: false, formStatus: 'success', alreadyAccepted: true})
      })
      .catch((err) => {
        this.setState({
          submitting: false,
          formStatus: 'failure',
          errorText: (err && String(err)) || 'invalidToken',
        })
      })
  }

  render() {
    const {t, classes} = this.props
    const {
      loading,
      submitting,
      formStatus,
      errorText,
      alreadyAccepted,
      acceptTerms,
      formError,
      links,
      tauroFactorisation,
      tauroTermsAccepted,
      tauroRulesOpen,
      cashbackDisclaimer,
      cashbackDisclaimerAccepted,
      requiresEuSolicitationConsent,
      solicitationAcceptance,
    } = this.state
    const companyName = config.name || 'Tauro Markets'

    return (
      <div className={classes.mainBody}>
        <HeaderDialog />
        <Grid container>
          <Hidden xsDown>
            <Grid item sm={3} />
          </Hidden>
          <Grid item sm={9} xs={12} className={classes.wrapper}>
            <PageTitle modal>
              <Trans
                {...(requiresEuSolicitationConsent
                  ? messages.solicitationAcceptanceTitle
                  : messages.partnerLegalConsentIntro)}
              />
            </PageTitle>

            {loading && (
              <Typography variant="body1">
                <Trans {...messages.loading} />
              </Typography>
            )}

            {!loading && formStatus === 'success' && (
              <NotificationBar status="success">
                <Trans {...messages.partnerLegalAcceptanceSuccess} />
              </NotificationBar>
            )}

            {!loading && formStatus === 'failure' && (
              <FormHelperText className={classes.errorMessage}>
                {errorText === 'invalidToken' ? (
                  <Trans {...messages.partnerLegalAcceptanceInvalidToken} />
                ) : (
                  errorText
                )}
              </FormHelperText>
            )}

            {!loading && !alreadyAccepted && (
              <Grid container spacing={1} className={classes.grid}>
                {requiresEuSolicitationConsent && (
                  <Grid item xs={12}>
                    <Typography variant="body1" paragraph>
                      <Trans
                        {...messages.solicitationAcceptancePart1}
                        components={[
                          <a
                            href="https://tauromarkets.com"
                            target="_blank"
                            rel="noreferrer noopener"
                            className={classes.link}
                          />,
                        ]}
                      />
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <Trans {...messages.solicitationAcceptancePart2} />
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <Trans {...messages.solicitationAcceptancePart3} />
                    </Typography>
                    <FormControlLabel
                      classes={{label: classes.checkboxLabel}}
                      control={
                        <Checkbox
                          color="primary"
                          checked={solicitationAcceptance}
                          onChange={(e) =>
                            this.setState({
                              solicitationAcceptance: e.target.checked,
                              formError: false,
                            })
                          }
                        />
                      }
                      label={
                        <Trans
                          {...messages.solicitationAcceptanceCheckbox}
                          components={[<span className={classes.link} />]}
                        />
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    classes={{label: classes.checkboxLabel}}
                    control={
                      <Checkbox
                        color="primary"
                        checked={acceptTerms}
                        onChange={(e) =>
                          this.setState({acceptTerms: e.target.checked, formError: false})
                        }
                      />
                    }
                    label={
                      <Trans
                        {...messages.signUpCheckbox}
                        values={{company: companyName}}
                        components={[
                          <a
                            href={links.terms}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={classes.link}
                          >
                            terms
                          </a>,
                          <a
                            href={links.legal}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={classes.link}
                          >
                            legal
                          </a>,
                          <a
                            href={links.privacy}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={classes.link}
                          >
                            privacy
                          </a>,
                        ]}
                      />
                    }
                  />
                  {formError && (
                    <FormHelperText className={classes.errorMessage}>
                      <Trans {...messages.requiredField} />
                    </FormHelperText>
                  )}
                </Grid>
                {!!tauroFactorisation && (
                  <Grid item xs={12}>
                    <Typography variant="caption" paragraph>
                      <Trans {...messages.tauroTermsDescription} />
                    </Typography>
                    <TauroTermsCheckbox
                      checked={tauroTermsAccepted}
                      onChange={(checked) =>
                        this.setState({tauroTermsAccepted: checked, formError: false})
                      }
                      onOpenRules={() => this.setState({tauroRulesOpen: true})}
                    />
                  </Grid>
                )}
                {!!cashbackDisclaimer && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      {cashbackDisclaimer.title}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <em>{cashbackDisclaimer.body}</em>
                    </Typography>
                    <FormControlLabel
                      classes={{label: classes.checkboxLabel}}
                      control={
                        <Checkbox
                          color="primary"
                          checked={cashbackDisclaimerAccepted}
                          onChange={(e) =>
                            this.setState({
                              cashbackDisclaimerAccepted: e.target.checked,
                              formError: false,
                            })
                          }
                        />
                      }
                      label={
                        <Trans
                          {...messages.partnerCashbackDisclaimerAccept}
                          values={{title: cashbackDisclaimer.title}}
                        />
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <LoadingButton
                    disabled={
                      submitting ||
                      !acceptTerms ||
                      (!!tauroFactorisation && !tauroTermsAccepted) ||
                      (!!cashbackDisclaimer && !cashbackDisclaimerAccepted) ||
                      (requiresEuSolicitationConsent && !solicitationAcceptance)
                    }
                    status={formStatus || ''}
                    onClick={() => this.handleSubmit()}
                    hideProgressBar={false}
                  >
                    {t(messages.confirm.i18nKey, messages.confirm.defaults)}
                  </LoadingButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <TauroRulesModal
          open={!!tauroRulesOpen}
          onClose={() => this.setState({tauroRulesOpen: false})}
          rules={tauroFactorisation}
        />
      </div>
    )
  }
}

export default withStyles(styles, {withTheme: true})(withNamespaces()(PartnerLegalAcceptance))
